import { ExternalLink, Check, Zap, Shield, Clock, Coins } from "lucide-react";
import { useAffiliateTracking } from "@/hooks/useAffiliateTracking";
import AffiliateDisclosure from "@/components/AffiliateDisclosure";

const exchanges = [
  {
    name: "Binance",
    emoji: "🟠",
    benefit: "Lowest trading fees",
    detail: "0.1% per trade",
    highlight: "1000+ coins available",
    icon: Coins,
    url: "https://www.binance.com/referral/earn-together/refer2earn-usdc/claim?hl=en-AU&ref=GRO_28502_5AZZR&utm_source=default",
    platform: "binance" as const,
  },
  {
    name: "CoinSpot",
    emoji: "🟢",
    benefit: "Instant PayID deposits",
    detail: "1% trading fee",
    highlight: "Beginner friendly",
    icon: Zap,
    url: "https://www.coinspot.com.au?affiliate=72JXN7",
    platform: "coinspot" as const,
  },
  {
    name: "Swyftx",
    emoji: "🔵",
    benefit: "Tightest spreads",
    detail: "0.6% trading fee",
    highlight: "Auto-invest features",
    icon: Shield,
    url: "https://trade.swyftx.com/register/?ref=davidrealestate",
    platform: "swyftx" as const,
  },
  {
    name: "CoinJar",
    emoji: "🟡",
    benefit: "Simple & intuitive",
    detail: "1% trading fee",
    highlight: "Best for first-timers",
    icon: Clock,
    url: "https://invite.coinjar.com/EIvL/join?af_referral_uid=Vubbelaar",
    platform: "coinjar" as const,
  },
];

export function ExchangeWidget() {
  const { trackAffiliateClick } = useAffiliateTracking();

  const handleClick = (platform: typeof exchanges[0]['platform'], url: string) => {
    trackAffiliateClick(platform);
    window.open(url, '_blank');
  };

  return (
    <div className="gradient-card rounded-2xl p-6 border border-border/50 shadow-elevation">
      <h3 className="text-lg font-bold text-foreground mb-4 flex items-center gap-2">
        <span>🏦</span> Where to Buy Crypto in Australia
      </h3>
      
      <div className="space-y-3">
        {exchanges.map((exchange) => {
          const Icon = exchange.icon;
          return (
            <button
              key={exchange.name}
              onClick={() => handleClick(exchange.platform, exchange.url)}
              className="w-full flex items-center justify-between bg-secondary hover:bg-secondary/80 rounded-xl p-4 transition-all group cursor-pointer"
            >
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-background flex items-center justify-center text-xl">
                  {exchange.emoji}
                </div>
                <div className="text-left">
                  <div className="font-semibold text-foreground group-hover:text-primary transition-colors flex items-center gap-2">
                    {exchange.name}
                    <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                  <div className="text-xs text-muted-foreground flex items-center gap-1">
                    <Icon className="w-3 h-3 text-primary" />
                    {exchange.benefit}
                  </div>
                </div>
              </div>
              <div className="text-right">
                <div className="text-sm font-semibold text-success">{exchange.detail}</div>
                <div className="text-xs text-muted-foreground">{exchange.highlight}</div>
              </div>
            </button>
          );
        })}
      </div>
      
      <AffiliateDisclosure className="mt-3" partners="Binance, CoinSpot, Swyftx and CoinJar" />

      <div className="mt-4 pt-4 border-t border-border/30 text-xs text-muted-foreground flex items-center gap-2">
        <Check className="w-4 h-4 text-success flex-shrink-0" />
        All AUSTRAC-registered • Australian-owned
      </div>
    </div>
  );
}
